/**
 * Date of Birth Formatter
 * 
 * Handles progressive formatting of dates in MM/DD/YYYY format with intelligent
 * validation, automatic slash insertion, and comprehensive date logic.
 */

import { BaseFormatter, FormatContext, ExtendedFormattingResult } from './base-formatter';
import { FormattingOptions, ValidationResult } from '../types/formatting-types';

/**
 * Date component validation ranges
 */
interface DateComponents {
  month: string;
  day: string;
  year: string;
}

/**
 * Date of birth formatter implementing MM/DD/YYYY format
 * 
 * Features:
 * - Progressive formatting with automatic slash insertion
 * - Intelligent date validation (leap years, month/day ranges)
 * - Age validation for date of birth contexts
 * - Real-time formatting during typing
 */
export class DateFormatter extends BaseFormatter {
  private readonly maxLength = 10; // MM/DD/YYYY
  private readonly minAge = 13;    // Minimum age for most services
  private readonly maxAge = 120;   // Maximum reasonable age

  constructor() {
    super('date', {
      dateFormat: 'MM/DD/YYYY'
    });
  }

  /**
   * Extract raw digits from formatted date
   */
  public extractRaw(formattedValue: string): string {
    return this.stripFormatting(formattedValue);
  }

  /**
   * Check if this formatter handles date fields
   */
  public canHandle(fieldType: string): boolean {
    return fieldType === 'date' || 
           fieldType === 'date_of_birth' || 
           fieldType === 'dob' ||
           fieldType === 'birth_date' ||
           fieldType === 'birthday';
  }

  /**
   * Get the expected format pattern
   */
  public getFormatPattern(): string {
    return 'MM/DD/YYYY';
  }

  /**
   * Sanitize input to only allow numeric characters
   */
  protected sanitizeInput(value: string): string {
    // Remove all non-numeric characters for processing
    return value.replace(/\D/g, '');
  }

  /**
   * Perform progressive date formatting
   */
  protected performFormat(context: FormatContext): ExtendedFormattingResult {
    const { rawValue, cursorPosition, isProgressive } = context;
    
    // Limit to 8 digits (MMDDYYYY)
    const digits = rawValue.substring(0, 8);
    
    let formattedValue = '';
    let newCursorPosition = cursorPosition;

    // Progressive formatting based on digit count
    if (digits.length === 0) {
      formattedValue = '';
    } else if (digits.length <= 2) {
      // Month: MM
      formattedValue = digits;
    } else if (digits.length <= 4) {
      // Month and day: MM/DD
      const month = digits.substring(0, 2);
      const day = digits.substring(2);
      formattedValue = `${month}/${day}`;
    } else {
      // Full format: MM/DD/YYYY
      const month = digits.substring(0, 2);
      const day = digits.substring(2, 4);
      const year = digits.substring(4);
      formattedValue = `${month}/${day}/${year}`;
    }

    // Calculate cursor position for progressive formatting
    if (isProgressive && cursorPosition !== undefined) {
      newCursorPosition = this.calculateDateCursorPosition(
        rawValue,
        digits,
        formattedValue,
        cursorPosition
      );
    }

    return {
      formattedValue,
      rawValue: digits,
      success: true,
      errors: [],
      newCursorPosition,
      wasModified: rawValue !== formattedValue
    };
  }

  /**
   * Validate date of birth
   */
  protected performValidation(rawValue: string, options?: FormattingOptions): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check for minimum length
    if (rawValue.length < 8) {
      errors.push('Date must be complete (MM/DD/YYYY)');
      return { isValid: false, errors, warnings };
    }

    // Parse date components
    const components = this.parseDateComponents(rawValue);
    if (!components) {
      errors.push('Invalid date format');
      return { isValid: false, errors, warnings };
    }

    // Validate month
    const monthNum = parseInt(components.month);
    if (monthNum < 1 || monthNum > 12) {
      errors.push('Month must be between 01 and 12');
    }

    // Validate day
    const dayNum = parseInt(components.day);
    if (dayNum < 1 || dayNum > 31) {
      errors.push('Day must be between 01 and 31');
    }

    // Validate year
    const yearNum = parseInt(components.year);
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - this.maxAge;
    const maxYear = currentYear - this.minAge;

    if (yearNum < minYear) {
      errors.push(`Year cannot be earlier than ${minYear}`);
    }

    if (yearNum > maxYear) {
      errors.push(`You must be at least ${this.minAge} years old`);
    }

    if (yearNum > currentYear) {
      errors.push('Date cannot be in the future');
    }

    // Advanced date validation if basic validation passes
    if (errors.length === 0) {
      const dateValidation = this.validateDate(monthNum, dayNum, yearNum);
      errors.push(...dateValidation.errors);
      warnings.push(...dateValidation.warnings || []);
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Check if input is a complete date
   */
  protected isCompleteInput(rawValue: string): boolean {
    return rawValue.length === 8;
  }

  /**
   * Check if character is valid for date input (numeric only)
   */
  protected isValidInputCharacter(char: string): boolean {
    return /\d/.test(char);
  }

  /**
   * Remove formatting to get raw digits
   */
  protected stripFormatting(value: string): string {
    return value.replace(/\D/g, '');
  }

  /**
   * Parse date components from raw digits
   */
  private parseDateComponents(rawValue: string): DateComponents | null {
    if (rawValue.length !== 8) {
      return null;
    }

    return {
      month: rawValue.substring(0, 2),
      day: rawValue.substring(2, 4),
      year: rawValue.substring(4, 8)
    };
  }

  /**
   * Advanced date validation with leap year and day-of-month checks
   */
  private validateDate(month: number, day: number, year: number): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // Check if date is valid using JavaScript Date object
      const date = new Date(year, month - 1, day);
      
      // Verify the date components match (JavaScript will adjust invalid dates)
      if (date.getFullYear() !== year || 
          date.getMonth() !== month - 1 || 
          date.getDate() !== day) {
        errors.push('Invalid date (check month and day combination)');
        return { isValid: false, errors, warnings };
      }

      // Specific month/day validations
      const daysInMonth = this.getDaysInMonth(month, year);
      if (day > daysInMonth) {
        if (month === 2 && day === 29) {
          errors.push(`${year} is not a leap year`);
        } else {
          errors.push(`${this.getMonthName(month)} has only ${daysInMonth} days`);
        }
      }

      // Check for common date entry mistakes
      if (month === day && month <= 12) {
        warnings.push('Month and day are the same - please verify');
      }

      // Check for very recent dates (might be entry error)
      const today = new Date();
      const enteredDate = new Date(year, month - 1, day);
      const daysDifference = Math.floor((today.getTime() - enteredDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDifference < (this.minAge * 365)) {
        warnings.push(`This date suggests age under ${this.minAge} years`);
      }

    } catch (error) {
      errors.push('Invalid date');
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Get number of days in a given month/year
   */
  private getDaysInMonth(month: number, year: number): number {
    return new Date(year, month, 0).getDate();
  }

  /**
   * Check if year is a leap year
   */
  private isLeapYear(year: number): boolean {
    return ((year % 4 === 0) && (year % 100 !== 0)) || (year % 400 === 0);
  }

  /**
   * Get month name for error messages
   */
  private getMonthName(month: number): string {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1] || 'Invalid';
  }

  /**
   * Calculate cursor position after date formatting
   */
  private calculateDateCursorPosition(
    originalRaw: string,
    newRaw: string,
    formatted: string,
    originalCursor: number
  ): number {
    // Account for slash insertion
    if (newRaw.length > 2 && originalCursor <= 2) {
      // Cursor before first slash
      return originalCursor;
    } else if (newRaw.length > 4 && originalCursor <= 4) {
      // Cursor around second slash position
      if (originalCursor === 2) return 3; // After first slash
      if (originalCursor === 3) return 4;
      if (originalCursor === 4) return 6; // After second slash
    }

    // For most cases, calculate based on digit position
    let digitCount = 0;
    for (let i = 0; i < formatted.length; i++) {
      const char = formatted[i];
      if (/\d/.test(char)) {
        digitCount++;
        if (digitCount === originalCursor + 1) {
          return i + 1;
        }
      }
    }

    // Default: place cursor at end
    return formatted.length;
  }

  /**
   * Format date for display purposes
   * 
   * @param dateDigits Raw date digits
   * @returns Formatted date string
   */
  public static formatDateForDisplay(dateDigits: string): string {
    const formatter = new DateFormatter();
    const result = formatter.format(dateDigits, {}, undefined, false);
    return result.formattedValue;
  }

  /**
   * Check if a string looks like a date
   * 
   * @param value String to check
   * @returns Whether it looks like a date
   */
  public static looksLikeDate(value: string): boolean {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 6 && digits.length <= 8;
  }

  /**
   * Parse date from various formats
   * 
   * @param input Date in any format
   * @returns Clean digit string (MMDDYYYY)
   */
  public static parseDate(input: string): string {
    // Remove slashes, spaces, dashes, and any non-numeric characters
    return input.replace(/\D/g, '');
  }

  /**
   * Calculate age from date of birth
   * 
   * @param dobDigits Date of birth in MMDDYYYY format
   * @returns Age in years
   */
  public static calculateAge(dobDigits: string): number | null {
    if (dobDigits.length !== 8) return null;

    try {
      const month = parseInt(dobDigits.substring(0, 2));
      const day = parseInt(dobDigits.substring(2, 4));
      const year = parseInt(dobDigits.substring(4, 8));

      const today = new Date();
      const birthDate = new Date(year, month - 1, day);

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    } catch {
      return null;
    }
  }
}